import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  CombinedGraphQLErrors,
  ServerError,
  from,
  split,
} from '@apollo/client';
import { ErrorLink } from '@apollo/client/link/error';
import { SetContextLink } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';

import env from 'config/env';
import { storage } from 'lib/storage';
import { STORAGE_KEYS } from 'lib/constants';

/** Read the persisted auth token (used by both the HTTP and WS transports). */
function getAuthToken() {
  return storage.get(STORAGE_KEYS.AUTH_TOKEN);
}

/**
 * Broadcast that the session is no longer valid. The AuthProvider listens for
 * this event and performs the logout + redirect, keeping this transport file
 * decoupled from React / the router.
 */
function notifyUnauthorized() {
  window.dispatchEvent(new CustomEvent('quro:unauthorized'));
}

/* -------------------------------------------------------------------------- */
/* Links                                                                      */
/* -------------------------------------------------------------------------- */

/** Attach the bearer token to every outgoing operation. */
const authLink = new SetContextLink((prevContext) => {
  const token = getAuthToken();
  return {
    headers: {
      ...prevContext.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
});

/** Centralised error logging + auth-expiry handling. */
const errorLink = new ErrorLink(({ error, operation }) => {
  const opName = operation?.operationName ?? 'operation';

  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach((gqlError) => {
      if (gqlError.extensions?.code === 'UNAUTHENTICATED') notifyUnauthorized();
      if (!env.isProduction) {
        // eslint-disable-next-line no-console
        console.error(`[GraphQL] ${opName}: ${gqlError.message}`);
      }
    });
    return;
  }

  if (ServerError.is(error)) {
    if (error.statusCode === 401) notifyUnauthorized();
    if (!env.isProduction) {
      // eslint-disable-next-line no-console
      console.error(`[Network] ${opName}:`, error.message);
    }
    return;
  }

  if (error && !env.isProduction) {
    // eslint-disable-next-line no-console
    console.error(`[Error] ${opName}:`, error.message);
  }
});

/**
 * Terminating HTTP link for queries + mutations.
 *
 * NOTE: To enable product image uploads (multipart), replace this with
 * `apollo-upload-client`'s `UploadHttpLink` (default export of
 * `apollo-upload-client/UploadHttpLink.mjs`) — it is a drop-in replacement that
 * also serialises File/Blob variables. Left as HttpLink until the
 * `VendorCatalogAddInput` image fields are confirmed.
 */
const httpLink = new HttpLink({ uri: env.graphqlHttpUrl });

/** Terminating WebSocket link for subscriptions (OTP, order-ready-for-pickup). */
const wsLink = new GraphQLWsLink(
  createClient({
    url: env.graphqlWsUrl,
    lazy: true,
    retryAttempts: 5,
    connectionParams: () => {
      const token = getAuthToken();
      return token ? { Authorization: `Bearer ${token}`, authToken: token } : {};
    },
  }),
);

/** Route subscriptions over the WebSocket link, everything else over HTTP. */
const transportLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  authLink.concat(httpLink),
);

/* -------------------------------------------------------------------------- */
/* Client                                                                     */
/* -------------------------------------------------------------------------- */

export const apolloClient = new ApolloClient({
  link: from([errorLink, transportLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: { fetchPolicy: 'cache-and-network' },
  },
});

export default apolloClient;
